import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  WallElement,
  AreaElement,
  ShapeElement,
  DoorElement,
  StairElement,
  Floor,
} from '../types';
import { getMaterialConfig, WallMaterialType } from './wallMaterialConfig';

/**
 * 根据材质获取渲染厚度
 * @param material 材质类型
 * @param thickness 用户设置的厚度
 * @returns 渲染用的厚度（米）
 */
function getRenderThickness(material: string, thickness: number): number {
  const baseThickness = thickness / 1000 / 3; // 默认厚度转换
  
  // 如果是特殊材质，按比例调整渲染厚度
  if (material === 'glass') {
    // 玻璃材质渲染得更薄一些，体现透明轻薄感
    return baseThickness * 0.6;
  } else if (material === 'metal') {
    // 金属材质略微减薄
    return baseThickness * 0.85;
  }
  
  return baseThickness;
}

/**
 * 创建墙面3D网格（带门开口）
 */
export function createWallMeshWithDoors(wall: WallElement, doors: DoorElement[]): THREE.Group {
  const group = new THREE.Group();
  group.name = wall.id;
  group.userData = { type: 'wall', elementId: wall.id };

  if (wall.points.length < 2) return group;

  // 获取材质配置
  const materialType = (wall.material as WallMaterialType) || 'concrete';
  const materialConfig = getMaterialConfig(materialType);

  // 计算渲染厚度（根据材质调整）
  const renderThickness = getRenderThickness(materialType, wall.thickness);
  
  // 墙面逻辑高度3000mm（3米），wall.height为3D渲染高度（默认750mm）
  const height = wall.height / 1000; // 直接使用配置的渲染高度

  // 材质默认颜色
  const defaultColor = new THREE.Color(materialConfig.color);
  
  // 如果用户选择了自定义颜色，使用混合（70%用户颜色 + 30%材质颜色）
  const userColor = new THREE.Color(wall.color);
  const lightColor = userColor.clone().lerp(defaultColor, 0.3);
  
  console.log(`🎨 墙体材质: ${materialConfig.name}, 颜色: ${wall.color || materialConfig.color} -> 渲染颜色: #${lightColor.getHexString()}`);
  
  // 获取透明度（材质配置优先，用户设置次之）
  const opacity = wall.opacity !== undefined ? wall.opacity : materialConfig.opacity;
  
  // 创建材质（根据材质类型设置不同的物理属性）
  const material = new THREE.MeshStandardMaterial({
    color: lightColor,
    side: THREE.DoubleSide,
    roughness: materialConfig.roughness,
    metalness: materialConfig.metalness,
    transparent: opacity < 1 || (materialConfig.transmission !== undefined),
    opacity: opacity,
    // 透光率（用于玻璃等材质）- 仅在定义时添加
    ...(materialConfig.transmission !== undefined && { transmission: materialConfig.transmission }),
    // 环境贴图反射强度 - 仅在定义时添加
    ...(materialConfig.envMapIntensity !== undefined && { envMapIntensity: materialConfig.envMapIntensity }),
    // 高透明度时关闭 depthWrite，避免深度写入冲突
    depthWrite: opacity >= 0.8,
  });

  // 为每段墙创建独立mesh，检查是否有门
  for (let i = 0; i < wall.points.length - 1; i++) {
    const p1 = wall.points[i];
    const p2 = wall.points[i + 1];

    // 计算墙段的长度和角度
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    // 让three中的Z方向与2D中的Y方向同向（向下为正），不再做Y轴镜像
    const angle = Math.atan2(dy, dx);

    // 查找该墙段上的所有门
    const doorsOnSegment = doors.filter(door => 
      door.wallId === wall.id && door.wallSegmentIndex === i
    );

    if (doorsOnSegment.length === 0) {
      // 没有门，直接渲染整段墙
      // 创建墙面几何体（使用BoxGeometry修复镜像问题）
      let width = Math.sqrt(
        Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
      ) / 100;
      
      // 扩展墙段长度以覆盖拐角处的缝隙
      const extension = renderThickness * 0.6; // 向两端延伸墙厚的60%
      const isFirstSegment = i === 0;
      const isLastSegment = i === wall.points.length - 2;
      
      // 非起点终点的墙段向两端延伸
      if (!isFirstSegment && !isLastSegment) {
        width += extension * 2;
      } else if (!isFirstSegment) {
        width += extension; // 只向起点延伸
      } else if (!isLastSegment) {
        width += extension; // 只向终点延伸
      }

      // 使用Box替代Plane避免镜像问题
      const geometry = new THREE.BoxGeometry(width, height, renderThickness);
      const mesh = new THREE.Mesh(geometry, material);

      // 计算中心点和旋转角度
      const centerX = (p1.x + p2.x) / 2 / 100;
      const centerZ = (p1.y + p2.y) / 2 / 100;
      const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

      // 设置位置和旋转
      mesh.position.set(centerX, height / 2, centerZ);
      mesh.rotation.y = -angle;
      group.add(mesh);
    } else {
      // 有门，需要分段渲染
      // 收集所有门的起止位置（0-1之间的参数）
      const doorSegments: { start: number; end: number }[] = [];
      
      for (const door of doorsOnSegment) {
        if (!door.points || door.points.length < 2) continue;
        
        // 计算门在墙段上的起止位置（投影到墙段上）
        const dp1 = door.points[0];
        const dp2 = door.points[1];
        
        // 计算投影参数 t (0-1)
        const t1 = ((dp1.x - p1.x) * dx + (dp1.y - p1.y) * dy) / (segmentLength * segmentLength);
        const t2 = ((dp2.x - p1.x) * dx + (dp2.y - p1.y) * dy) / (segmentLength * segmentLength);
        
        const start = Math.min(t1, t2);
        const end = Math.max(t1, t2);
        
        doorSegments.push({ start: Math.max(0, start), end: Math.min(1, end) });
      }
      
      // 排序门段
      doorSegments.sort((a, b) => a.start - b.start);
      
      // 渲染门之间的墙段
      let currentPos = 0;
      
      for (const doorSeg of doorSegments) {
        if (currentPos < doorSeg.start) {
          // 渲染门前的墙段
          const segStart = currentPos;
          const segEnd = doorSeg.start;
          const segLen = (segEnd - segStart) * segmentLength / 100;
          
          if (segLen > 0.01) { // 至少1cm才渲染
            const geometry = new THREE.BoxGeometry(segLen, height, renderThickness);
            const mesh = new THREE.Mesh(geometry, material);
            
            // 计算墙段中心位置
            const t = (segStart + segEnd) / 2;
            const cx = (p1.x + t * dx) / 100;
            const cz = (p1.y + t * dy) / 100;
            
            mesh.position.set(cx, height / 2, cz);
            mesh.rotation.y = -angle;
            group.add(mesh);
          }
        }
        currentPos = doorSeg.end;
      }
      
      // 渲染最后一段墙（最后一个门之后）
      if (currentPos < 1) {
        const segStart = currentPos;
        const segEnd = 1;
        const segLen = (segEnd - segStart) * segmentLength / 100;
        
        if (segLen > 0.01) {
          const geometry = new THREE.BoxGeometry(segLen, height, renderThickness);
          const mesh = new THREE.Mesh(geometry, material);
          
          const t = (segStart + segEnd) / 2;
          const cx = (p1.x + t * dx) / 100;
          const cz = (p1.y + t * dy) / 100;
          
          mesh.position.set(cx, height / 2, cz);
          mesh.rotation.y = -angle;
          group.add(mesh);
        }
      }
    }
  }

  return group;
}

/**
 * 创建墙面3D网格（向后兼容）
 */
export function createWallMesh(wall: WallElement): THREE.Group {
  return createWallMeshWithDoors(wall, []);
}

/**
 * 创建简单文字精灵（用于区域和家具标签）
 */
function createSimpleTextSprite(text: string, color: string = '#000000'): THREE.Sprite {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return new THREE.Sprite();

  // 设置画布尺寸
  canvas.width = 512;
  canvas.height = 128;

  // 设置字体样式（普通字体，不加粗）
  context.font = '40px Arial';
  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  // 绘制背景
  const padding = 16;
  const textMetrics = context.measureText(text);
  const textWidth = textMetrics.width;
  const bgWidth = textWidth + padding * 2;
  const bgHeight = 64;
  const bgX = (canvas.width - bgWidth) / 2;
  const bgY = (canvas.height - bgHeight) / 2;

  // 半透明白色背景
  context.fillStyle = 'rgba(255, 255, 255, 0.9)';
  context.fillRect(bgX, bgY, bgWidth, bgHeight);

  // 绘制文字（不绘制边框）
  context.fillStyle = color;
  context.fillText(text, canvas.width / 2, canvas.height / 2);

  // 创建纹理
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  // 创建精灵材质
  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });

  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.scale.set(2.5, 0.625, 1); // 调整大小（稍小一些）

  return sprite;
}

/**
 * 创建区域3D网格（地面 + 文字标签）
 * 
 * 区域层级系统：
 * - 每个区域分配一个递增的层级值（1, 2, 3, 4...）
 * - 使用 renderOrder 控制渲染顺序，层级高的后渲染（在上面）
 * - Y轴偏移：每个层级增加 0.001m（1mm），确保深度缓冲有足够差异
 * - 基于ID的哈希偏移确保同一层级但不同ID的区域也有微小差异
 */
export function createAreaMesh(area: AreaElement, areaLevel: number = 0): THREE.Group {
  const group = new THREE.Group();
  group.name = area.id;
  group.userData = { type: 'area', elementId: area.id, areaLevel };

  if (area.points.length < 3) {
    return group;
  }

  // 创建Shape（不需要预先反转Y，旋转会自动处理）
  const shape = new THREE.Shape();
  shape.moveTo(area.points[0].x / 100, area.points[0].y / 100);
  for (let i = 1; i < area.points.length; i++) {
    shape.lineTo(area.points[i].x / 100, area.points[i].y / 100);
  }
  shape.closePath();

  // 创建几何体
  const geometry = new THREE.ShapeGeometry(shape);
  
  // Y轴偏移量计算：
  // - 层级偏移：每个层级增加 0.001m（1mm），确保深度缓冲有足够差异
  // - 基于ID的哈希偏移：确保同一层级但不同ID的区域也有微小差异（0~0.0005m）
  const levelYOffset = areaLevel * 0.001;  // 每个层级增加 1mm
  const idHash = area.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const idYOffset = (idHash % 50) / 100000;  // 0 ~ 0.0005m 的微小差异
  const totalYOffset = levelYOffset + idYOffset;

  const material = new THREE.MeshStandardMaterial({
    color: area.color,
    side: THREE.DoubleSide,
    opacity: area.fillOpacity,
    transparent: area.fillOpacity < 1,
    roughness: 0.95,
    metalness: 0,
    // 高透明度时关闭 depthWrite，避免深度写入冲突
    depthWrite: area.fillOpacity >= 0.8,
  });

  const mesh = new THREE.Mesh(geometry, material);
  // 让区域与墙体保持同一坐标系：X 向右，Z 与 2D 的 Y 同向
  // Shape 在 XY 平面定义，旋转 +90° 到 XZ 平面
  mesh.rotation.x = Math.PI / 2;
  
  // 校准边界区域在地面，普通区域在地面上方
  const baseY = area.properties?.isCalibrationBoundary === true ? 0 : totalYOffset;
  mesh.position.y = baseY;
  
  // 使用层级值作为渲染顺序，层级越高渲染越晚（在上面）
  // 乘以一个大基数确保层级之间有明显间隔
  mesh.renderOrder = areaLevel * 1000;
  
  group.add(mesh);

  // 如果有区域名称且需要显示，添加文字标签
  if (area.areaName && area.showName !== false) {
    // 计算区域中心点
    let centerX = 0;
    let centerZ = 0;
    for (const point of area.points) {
      centerX += point.x / 100;
      centerZ += point.y / 100;
    }
    centerX /= area.points.length;
    centerZ /= area.points.length;

    // 创建文字精灵
    const textSprite = createSimpleTextSprite(area.areaName, area.color);
    textSprite.position.set(centerX, 0.5, centerZ); // 设置在区域中心，略高于地面
    group.add(textSprite);
  }

  return group;
}

/**
 * 创建形状3D网格
 */
export function createShapeMesh(shape: ShapeElement): THREE.Mesh {
  let geometry: THREE.BufferGeometry;
  // 家具高度按照与3米墙面的比例计算渲染高度
  const LOGICAL_WALL_HEIGHT = 3000; // 逻辑墙高3米
  const RENDER_WALL_HEIGHT = 0.375; // 渲染墙高375mm = 0.375m（原750mm的一半）
  const furnitureLogicalHeight = shape.height || 750; // 家具真实高度（mm）
  const height = (furnitureLogicalHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT; // 按比例计算渲染高度
  
  // 计算离地高度（cm → 米，按比例缩放）
  // elevation单位是cm，需要先转为mm再按比例缩放
  const elevationCm = (shape as any).elevation || 0;
  const elevationMm = elevationCm * 10; // cm → mm
  const elevation = (elevationMm / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;

  if (shape.type === 'rectangle' && shape.points.length >= 4) {
    // 矩形 - 使用所有4个角点计算边界
    const minX = Math.min(...shape.points.map(p => p.x));
    const maxX = Math.max(...shape.points.map(p => p.x));
    const minY = Math.min(...shape.points.map(p => p.y));
    const maxY = Math.max(...shape.points.map(p => p.y));
    
    // 如果是家具，使用实际尺寸；否则使用拖拽尺寸
    let width: number;
    let depth: number;
    
    if (shape.furnitureName && shape.width && shape.depth) {
      // 家具：使用设定的尺寸
      width = shape.width / 1000; // mm to meters
      depth = shape.depth / 1000; // mm to meters
    } else {
      // 普通矩形：使用边界框尺寸
      width = Math.abs(maxX - minX) / 100;
      depth = Math.abs(maxY - minY) / 100;
    }

    geometry = new THREE.BoxGeometry(width, height, depth);

    // 读取透明度，默认0.9
    const shapeOpacity = (shape as any).opacity ?? 0.9;
    const material = new THREE.MeshStandardMaterial({
      color: shape.color,
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: shapeOpacity,
      // 移除 polygonOffset，完全依赖 Y 轴分离解决深度冲突
    });

    // 计算中心点（使用边界框中心）
    const centerX = (minX + maxX) / 2 / 100;
    const centerZ = (minY + maxY) / 2 / 100;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = shape.id;
    mesh.userData = { type: 'shape', elementId: shape.id };

    mesh.position.set(centerX, height / 2 + elevation, centerZ);

    // 如果是家具，添加名称标签
    if (shape.furnitureName) {
      const group = new THREE.Group();
      group.name = shape.id;
      group.userData = { type: 'shape', elementId: shape.id };
      group.add(mesh);

      // 创建文字精灵
      const textSprite = createSimpleTextSprite(shape.furnitureName, shape.color);
      textSprite.position.set(0, height + 0.3, 0); // 设置在家具上方
      group.add(textSprite);

      group.position.set(centerX, elevation, centerZ);
      mesh.position.set(0, height / 2, 0);

      return group as any;
    }

    return mesh;
  } else if (shape.type === 'circle' && shape.points.length >= 2) {
    // 圆形/圆柱
    const center = shape.points[0];
    const edge = shape.points[1];
    
    // 如果是圆形设备，使用实际半径；否则使用拖拽半径
    let radius: number;
    
    if (shape.furnitureName && shape.radius) {
      // 圆形设备：使用设定的半径
      radius = shape.radius / 1000; // mm to meters
    } else {
      // 普通圆形：使用拖拽半径
      radius = Math.sqrt(
        Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2)
      ) / 100;
    }

    geometry = new THREE.CylinderGeometry(radius, radius, height, 32);

    // 读取透明度，默认0.9
    const circleOpacity = (shape as any).opacity ?? 0.9;
    const material = new THREE.MeshStandardMaterial({
      color: shape.color,
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: circleOpacity,
      // 移除 polygonOffset，完全依赖 Y 轴分离解决深度冲突
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = shape.id;
    mesh.userData = { type: 'shape', elementId: shape.id };
    mesh.position.set(center.x / 100, height / 2 + elevation, center.y / 100);

    // 如果是圆形设备，添加名称标签
    if (shape.furnitureName) {
      const group = new THREE.Group();
      group.name = shape.id;
      group.userData = { type: 'shape', elementId: shape.id };
      group.add(mesh);

      // 创建文字精灵
      const textSprite = createSimpleTextSprite(shape.furnitureName, shape.color);
      textSprite.position.set(0, height + 0.3, 0); // 设置在圆柱上方
      group.add(textSprite);

      group.position.set(center.x / 100, elevation, center.y / 100);
      mesh.position.set(0, height / 2, 0);

      return group as any;
    }

    return mesh;
  } else if (shape.type === 'polygon' && shape.points.length >= 3) {
    // 多边形 - 使用 ExtrudeGeometry 创建带高度的挤压形状
    const polygonShape = new THREE.Shape();
    // 2D的(x, y)直接用于Shape，ExtrudeGeometry会自动处理坐标系转换
    polygonShape.moveTo(shape.points[0].x / 100, shape.points[0].y / 100);
    for (let i = 1; i < shape.points.length; i++) {
      polygonShape.lineTo(shape.points[i].x / 100, shape.points[i].y / 100);
    }
    polygonShape.closePath();

    // 挤压设置：depth 就是高度，沿 Z 轴挤压
    const extrudeSettings = {
      depth: height,
      bevelEnabled: false,
    };

    geometry = new THREE.ExtrudeGeometry(polygonShape, extrudeSettings);

    // 读取透明度，默认0.9
    const polygonOpacity = (shape as any).opacity ?? 0.9;
    const material = new THREE.MeshStandardMaterial({
      color: shape.color,
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: polygonOpacity,
      side: THREE.DoubleSide,
      // 移除 polygonOffset，完全依赖 Y 轴分离解决深度冲突
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = shape.id;
    mesh.userData = { type: 'shape', elementId: shape.id };

    // ExtrudeGeometry 沿 Z 轴挤压，旋转使多边形水平放置
    // 原始形状在 XY 平面，挤压后沿 Z 轴
    // 旋转 Math.PI / 2 后，Z 轴变为 Y 轴，形状水平放置
    mesh.rotation.x = Math.PI / 2;

    // 设置位置：elevation 是离地高度，height/2 是因为挤压几何体的中心在中间
    mesh.position.y = elevation;

    // 如果是多边形设备，添加名称标签
    if (shape.furnitureName) {
      // 计算多边形中心点
      let centerX = 0;
      let centerZ = 0;
      for (const point of shape.points) {
        centerX += point.x;
        centerZ += point.y;
      }
      centerX /= shape.points.length;
      centerZ /= shape.points.length;

      const group = new THREE.Group();
      group.name = shape.id;
      group.userData = { type: 'shape', elementId: shape.id };
      group.position.y = elevation; // 应用离地高度到组
      mesh.position.y = 0; // 重置mesh的y位置（因为组已经设置了elevation）
      group.add(mesh);

      // 创建文字精灵
      const textSprite = createSimpleTextSprite(shape.furnitureName, shape.color);
      textSprite.position.set(centerX / 100, height + 0.3, centerZ / 100);
      group.add(textSprite);

      return group as any;
    }

    return mesh;
  }

  // 默认返回空网格
  return new THREE.Mesh();
}

// 辅助函数：获取元素类型的中文标签（与RightPanel保持一致）
// 注意：这里的返回值是给 hiddenCategories 用的，保持中文标签
const getElementTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    wall: '墙体',
    door: '门',
    window: '窗',
    area: '区域',
    furniture: '家具',
    circle: '圆形',
    polygon: '多边形',
    stair: '楼梯',
    shape: '图形',
    text: '文本',
    model3d: '3D模型',
  };
  return typeLabels[type] || type;
};

/**
 * 创建楼层的所有3D网格
 * 
 * 层级系统说明：
 * - 墙体：默认层级 (最底层，renderOrder = 0)
 * - 区域：层级 1~999（按添加顺序递增，用于区分不同区域）
 * - 形状（家具等）：层级 1000~1999
 * - 楼梯、门：层级 2000+
 * - 3D模型：层级 3000+（始终在所有区域之上）
 */
export function createFloorMeshes(floor: Floor, hiddenCategories?: Record<string, boolean>): THREE.Group {
  const floorGroup = new THREE.Group();
  floorGroup.name = `floor-${floor.id}`;

  // 首先收集所有门的信息（不受hiddenCategories影响，因为门是墙的一部分）
  const doors: DoorElement[] = [];
  for (const layer of floor.layers) {
    if (!layer.visible) continue;
    for (const element of layer.elements) {
      if (element.type === 'door') {
        doors.push(element as DoorElement);
      }
    }
  }

  // 遍历所有图层和元素
  let areaLevel = 1; // 区域层级从1开始递增，最大999
  for (const layer of floor.layers) {
    if (!layer.visible) continue;

    for (const element of layer.elements) {
      // 检查元素类别是否被隐藏
      const typeLabel = getElementTypeLabel(element.type);
      if (hiddenCategories && hiddenCategories[typeLabel]) {
        continue; // 跳过隐藏的类别
      }

      let mesh: THREE.Object3D;

      switch (element.type) {
        case 'wall':
          // 传递门信息给墙体渲染函数
          mesh = createWallMeshWithDoors(element as WallElement, doors);
          break;
        case 'area':
          mesh = createAreaMesh(element as AreaElement, areaLevel++);
          break;
        case 'rectangle':
        case 'circle':
        case 'polygon':
          mesh = createShapeMesh(element as ShapeElement);
          break;
        case 'door':
          mesh = createDoorMesh(element as DoorElement);
          break;
        case 'stair':
          mesh = createStairMesh(element as StairElement);
          break;
        default:
          continue;
      }

      floorGroup.add(mesh);
    }
  }

  return floorGroup;
}

/**
 * 设置场景光照
 */
export function setupLights(scene: THREE.Scene): void {
  // 环境光
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  // 主光源
  const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight1.position.set(10, 20, 10);
  directionalLight1.castShadow = true;
  directionalLight1.shadow.camera.left = -50;
  directionalLight1.shadow.camera.right = 50;
  directionalLight1.shadow.camera.top = 50;
  directionalLight1.shadow.camera.bottom = -50;
  scene.add(directionalLight1);

  // 辅助光源
  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
  directionalLight2.position.set(-10, 15, -10);
  scene.add(directionalLight2);

  // 半球光
  const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
  scene.add(hemisphereLight);
}

/**
 * 创建网格辅助线
 */
export function createGridHelper(size: number = 50, divisions: number = 50): THREE.GridHelper {
  const gridHelper = new THREE.GridHelper(size, divisions, 0x888888, 0xcccccc);
  return gridHelper;
}

/**
 * 创建坐标轴辅助线
 */
export function createAxesHelper(size: number = 10): THREE.AxesHelper {
  const axesHelper = new THREE.AxesHelper(size);
  return axesHelper;
}

/**
 * 自动调整相机以适应场景，使模型占满画布的指定比例
 */
export function fitCameraToScene(
  camera: THREE.PerspectiveCamera,
  controls: OrbitControls,
  scene: THREE.Scene,
  targetCoverage: number = 0.85  // 目标占画布比例，默认85%
): void {
  // 打印场景中的所有对象
  console.log('📷 场景对象数量:', scene.children.length);
  scene.children.forEach((child, index) => {
    console.log(`  [${index}] ${child.type}: ${child.name || '(匿名)'}`);
    // 如果是 Group，打印其子对象
    if (child instanceof THREE.Group && child.children.length > 0) {
      child.children.forEach((subChild, subIndex) => {
        console.log(`      [${subIndex}] ${subChild.type}: ${subChild.name || '(匿名)'}, position=(${subChild.position.x.toFixed(2)}, ${subChild.position.y.toFixed(2)}, ${subChild.position.z.toFixed(2)})`);
      });
    }
  });

  const box = new THREE.Box3().setFromObject(scene);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  console.log('📷 场景边界框:', {
    min: { x: box.min.x.toFixed(2), y: box.min.y.toFixed(2), z: box.min.z.toFixed(2) },
    max: { x: box.max.x.toFixed(2), y: box.max.y.toFixed(2), z: box.max.z.toFixed(2) },
    size: { x: size.x.toFixed(2), y: size.y.toFixed(2), z: size.z.toFixed(2) },
    center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) }
  });

  // 检测场景尺寸
  const maxDim = Math.max(size.x, size.y, size.z);

  // 如果场景太小或只有网格辅助线，添加一个默认大小的边界
  if (maxDim < 1) {
    console.warn('⚠️ 场景边界太小，扩展为默认大小');
    // 使用一个合理默认大小（10x10）
    const defaultSize = 10;
    box.setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(defaultSize, defaultSize * 0.3, defaultSize)
    );
    const newSize = box.getSize(new THREE.Vector3());
    const newCenter = box.getCenter(new THREE.Vector3());
    console.log('📷 使用默认边界:', {
      size: { x: newSize.x.toFixed(2), y: newSize.y.toFixed(2), z: newSize.z.toFixed(2) },
      center: { x: newCenter.x.toFixed(2), y: newCenter.y.toFixed(2), z: newCenter.z.toFixed(2) }
    });
  }

  const effectiveBox = box;
  const effectiveSize = effectiveBox.getSize(new THREE.Vector3());
  const effectiveCenter = effectiveBox.getCenter(new THREE.Vector3());
  const effectiveMaxDim = Math.max(effectiveSize.x, effectiveSize.y, effectiveSize.z);

  console.log('📷 有效场景尺寸:', effectiveMaxDim.toFixed(2));

  // 计算相机距离，使场景占据画布的指定比例
  const fov = camera.fov * (Math.PI / 180);
  let cameraDistance = Math.abs(effectiveMaxDim / 2 / Math.tan(fov / 2) / targetCoverage);

  console.log('📷 计算相机距离:', {
    fov: (fov * 180 / Math.PI).toFixed(1) + '°',
    targetCoverage: (targetCoverage * 100).toFixed(0) + '%',
    rawDistance: cameraDistance.toFixed(2)
  });

  // 限制距离范围
  cameraDistance = Math.max(1, Math.min(cameraDistance, 500));

  // 设置相机位置 - 从正前方上方俯视
  const direction = new THREE.Vector3(1, 0.6, 1).normalize();
  const cameraPos = effectiveCenter.clone().add(direction.multiplyScalar(cameraDistance));

  console.log('📷 相机设置详情:');
  console.log(`  📷 相机方向向量: (${direction.x.toFixed(2)}, ${direction.y.toFixed(2)}, ${direction.z.toFixed(2)})`);
  console.log(`  📷 场景中心点 (camera target): (${effectiveCenter.x.toFixed(2)}, ${effectiveCenter.y.toFixed(2)}, ${effectiveCenter.z.toFixed(2)})`);
  console.log(`  📷 相机距离: ${cameraDistance.toFixed(2)}`);
  console.log(`  📷 相机位置: (${cameraPos.x.toFixed(2)}, ${cameraPos.y.toFixed(2)}, ${cameraPos.z.toFixed(2)})`);

  camera.position.copy(cameraPos);
  camera.lookAt(effectiveCenter);
  controls.target.copy(effectiveCenter);
  controls.update();

  console.log('✅ 相机设置完成:', {
    targetCoverage: (targetCoverage * 100).toFixed(0) + '%',
    distance: cameraDistance.toFixed(2),
    position: { x: camera.position.x.toFixed(2), y: camera.position.y.toFixed(2), z: camera.position.z.toFixed(2) },
    target: { x: effectiveCenter.x.toFixed(2), y: effectiveCenter.y.toFixed(2), z: effectiveCenter.z.toFixed(2) }
  });
}

/**
 * 创建门3D网格
 */
export function createDoorMesh(door: DoorElement): THREE.Group {
  const group = new THREE.Group();
  group.name = door.id;
  group.userData = { type: 'door', elementId: door.id };

  // 检查points是否存在
  if (!door.points || door.points.length < 2 || !door.points[0] || !door.points[1]) {
    return group;
  }

  const p1 = door.points[0];
  const p2 = door.points[1];
  
  // 计算门的宽度（两点之间的距离）
  const doorWidth = Math.sqrt(
    (p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2
  ) / 100;
  
  if (doorWidth === 0 || isNaN(doorWidth)) {
    return group;
  }
  // 门在3D中表现为墙面的断开/开口，不需要额外渲染几何体
  // 墙体渲染时会自动在门的位置留出空缺
  // 这里返回空的 Group 即可
  
  return group;
}

/**
 * 创建楼梯3D网格
 */
export function createStairMesh(stair: StairElement): THREE.Group {
  const group = new THREE.Group();
  group.name = stair.id;
  group.userData = { type: 'stair', elementId: stair.id };

  const p1 = stair.points[0];
  const p2 = stair.points[1];
  const width = Math.abs(p2.x - p1.x) / 100;
  const length = Math.abs(p2.y - p1.y) / 100;
  const totalHeight = (stair.height || 3000) / 1000;
  const steps = stair.steps || 15;

  const stepHeight = totalHeight / steps;
  const stepDepth = length / steps;

  // 创建每个台阶
  for (let i = 0; i < steps; i++) {
    const geometry = new THREE.BoxGeometry(width, stepHeight, stepDepth);
    const material = new THREE.MeshStandardMaterial({
      color: '#ea580c',
      roughness: 0.95,  // 高粗糙度，减少反光
      metalness: 0,     // 非金属材质
    });

    const step = new THREE.Mesh(geometry, material);
    
    const currentHeight = i * stepHeight;
    const currentDepth = i * stepDepth;
    
    step.position.set(
      0,
      currentHeight + stepHeight / 2,
      stair.direction === 'up' ? -currentDepth - stepDepth / 2 : currentDepth + stepDepth / 2
    );

    group.add(step);
  }

  const centerX = (p1.x + p2.x) / 2 / 100;
  const centerZ = (p1.y + p2.y) / 2 / 100;
  group.position.set(centerX, 0, centerZ);

  return group;
}

export { OrbitControls };


/**
 * 创建文本Sprite用于3D场景
 */
export function createTextSprite(textElement: {
  content: string;
  fontSize: number;
  color: string;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  align: 'left' | 'center' | 'right';
  borderColor: string;
  backgroundColor: string;
  backgroundOpacity: number;
  padding: number;
  position: { x: number; y: number; z: number };
}): THREE.Sprite {
  // 创建高分辨率canvas用于文本纹理
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  
  // 使用更大的canvas尺寸以提高清晰度
  canvas.width = 1024;
  canvas.height = 512;
  
  // 配置字体（放大2.2倍以提高清晰度）
  const fontSize = textElement.fontSize * 2.2;
  ctx.font = `${textElement.italic ? 'italic ' : ''}${textElement.bold ? 'bold ' : ''}${fontSize}px ${textElement.fontFamily}`;
  ctx.textAlign = textElement.align;
  ctx.textBaseline = 'middle';
  
  // 测量文本
  const metrics = ctx.measureText(textElement.content);
  const textWidth = metrics.width;
  const textHeight = fontSize * 1.2;
  
  // 计算背景尺寸
  const padding = textElement.padding * 4;
  const bgWidth = textWidth + padding * 2;
  const bgHeight = textHeight + padding * 2;
  
  // 计算居中位置
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const bgX = centerX - bgWidth / 2;
  const bgY = centerY - bgHeight / 2;
  
  // 绘制阴影效果以增强可读性
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  
  // 绘制背景
  ctx.fillStyle = textElement.backgroundColor;
  ctx.globalAlpha = Math.max(textElement.backgroundOpacity, 0.9); // 确保背景足够不透明
  ctx.fillRect(bgX, bgY, bgWidth, bgHeight);
  
  // 重置阴影
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // 绘制边框（加粗以提高可见性）
  ctx.strokeStyle = textElement.borderColor;
  ctx.lineWidth = 6;
  ctx.globalAlpha = 1;
  ctx.strokeRect(bgX, bgY, bgWidth, bgHeight);
  
  // 绘制文本轮廓以增强对比度
  ctx.strokeStyle = textElement.color === '#000000' ? '#FFFFFF' : '#000000';
  ctx.lineWidth = 2;
  ctx.strokeText(textElement.content, centerX, centerY);
  
  // 绘制文本
  ctx.fillStyle = textElement.color;
  ctx.fillText(textElement.content, centerX, centerY);
  
  // 创建纹理和sprite
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    sizeAttenuation: false, // 禁用距离衰减，使文本保持固定像素大小
  });

  const sprite = new THREE.Sprite(material);

  // 设置位置
  sprite.position.set(
    textElement.position.x,
    textElement.position.y,
    textElement.position.z
  );

  // 设置缩放比例，使最终显示的字体大小等于用户选择的字体大小
  // canvas.height = 512，sprite.scale.y * canvas.height / 2 = 最终显示高度（像素）
  // 所以 scale.y = fontSize * 2 / canvas.height
  const scaleY = textElement.fontSize * 2 / canvas.height;
  sprite.scale.set(scaleY * 2, scaleY, 1);

  // 设置渲染顺序为最大值，确保文本始终在最上层
  sprite.renderOrder = 999999;

  return sprite;
}
